// TaskPage.jsx
import React, { useState, useMemo } from "react";
import T from '../../components/common/T';
import { useNavigate, useSearchParams } from "react-router-dom";
import TaskListView from "./TaskListView";
import TaskKanbanView from "./TaskKanbanView";
import TaskCalendarView from "./TaskCalendarView";
import { useTasks } from "../../context/TaskContext";
import { useProjects } from "../../context/ProjectContext";
import { useAuth } from "../../context/AuthContext";
import useModuleFilters from "../../hooks/useModuleFilters";
import RoleGate from "../../components/RoleGate";
import PageLayout, { PageHeader, StatsGrid, Toolbar, FilterPanel, TableCard } from "../../components/common/PageLayout";
import ViewSwitcher from "../../components/common/ViewSwitcher";
import { TopPrimaryButton } from "../../components/common/StandardButtons";
import {
  ClipboardDocumentListIcon, ClockIcon, ArrowPathIcon,
  CheckCircleIcon, ListBulletIcon, Squares2X2Icon, CalendarDaysIcon
} from "@heroicons/react/24/outline";

const TaskPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tasks, counts, fetchTasks } = useTasks();
  const { projects } = useProjects();
  const { checkAccess } = useAuth();

  React.useEffect(() => {
    const tab = searchParams.get("tab");
    if ((tab === "timesheets" || tab === "daily-logs" || tab === "daily-log") && tasks && tasks.length > 0) {
      navigate(`/admin/task/view/${tasks[0].id}?tab=timesheets`, { replace: true });
    }
  }, [searchParams, tasks, navigate]);

  const [view, setView] = useState("list");
  const {
    showFilter,
    toggleFilters: handleToggleFilters,
    filterOptions,
    loadingFilters,
    clearFilters,
    clearingFilters,
  } = useModuleFilters("tasks", {
    projects: [],
    statuses: [],
    priorities: [],
  });

  const [filters, setFilters] = useState({ status: "", priority: "", projectId: "" });
  const [searchTerm, setSearchTerm] = useState("");

  const handleClearAll = () => {
    setFilters({ status: "", priority: "", projectId: "" });
    setSearchTerm("");
    if (fetchTasks) fetchTasks({});
  };

  // ── Debounced Server API Call for Search & Filters (Visible in DevTools Network Tab) ──
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (fetchTasks) {
        const queryParams = {};
        if (searchTerm.trim()) queryParams.search = searchTerm.trim();
        if (filters.status) queryParams.status = filters.status;
        if (filters.priority) queryParams.priority = filters.priority;
        if (filters.projectId) queryParams.projectId = filters.projectId;

        fetchTasks(queryParams);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [searchTerm, filters, fetchTasks]);

  const [baselineTotal, setBaselineTotal] = useState(null);

  // Preserve initial baseline total when no status filter is applied
  React.useEffect(() => {
    if (counts?.total && (!filters.status || filters.status === "")) {
      setBaselineTotal(counts.total);
    }
  }, [counts?.total, filters.status]);

  const filteredTasks = Array.isArray(tasks) ? tasks : [];

  // ── Stats derived from API counts or fallback to ALL tasks ────────────────
  const allTasks = Array.isArray(tasks) ? tasks : [];
  const stats = useMemo(() => [
    {
      label: "Total Tasks",
      value: counts?.total || baselineTotal || allTasks.length,
      icon: ClipboardDocumentListIcon,
      variant: "default",
      isActive: !filters.status || filters.status === "",
      onClick: () => setFilters(f => ({ ...f, status: "" })),
    },
    {
      label: "Pending",
      value: counts?.pending ?? allTasks.filter(t => (t.status || "").trim().toLowerCase() === "pending").length,
      icon: ClockIcon,
      variant: "orange",
      isActive: filters.status === "Pending",
      onClick: () => setFilters(f => ({ ...f, status: f.status === "Pending" ? "" : "Pending" })),
    },
    {
      label: "In Progress",
      value: counts?.inProgress ?? counts?.in_progress ?? allTasks.filter(t => {
        const s = (t.status || "").trim().toLowerCase();
        return s === "in progress" || s === "inprogress" || s === "in_progress";
      }).length,
      icon: ArrowPathIcon,
      variant: "blue",
      isActive: filters.status === "In Progress",
      onClick: () => setFilters(f => ({ ...f, status: f.status === "In Progress" ? "" : "In Progress" })),
    },
    {
      label: "Completed",
      value: counts?.completed ?? allTasks.filter(t => (t.status || "").trim().toLowerCase() === "completed").length,
      icon: CheckCircleIcon,
      variant: "green",
      isActive: filters.status === "Completed",
      onClick: () => setFilters(f => ({ ...f, status: f.status === "Completed" ? "" : "Completed" })),
    },
  ], [allTasks, counts, filters.status]);

  return (
    <PageLayout>
      {/* Header */}
      <PageHeader
        icon={ClipboardDocumentListIcon}
        iconColor="text-[#E8612C]"
        title="Task Management"
        subtitle="Manage and track tasks across all projects"
        onRefresh={() => fetchTasks && fetchTasks()}
      >
        <RoleGate permission="task.create">
          <TopPrimaryButton
            onClick={() => navigate("/admin/task/add")}
            label="Add Task"
          />
        </RoleGate>
      </PageHeader>

      {/* Stats */}
      <StatsGrid stats={stats} cols={4} />

      {/* Toolbar */}
      <Toolbar
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        searchPlaceholder="Search tasks..."
        onToggleFilters={handleToggleFilters}
        showFilters={showFilter}
      >
        <ViewSwitcher
          view={view}
          onViewChange={setView}
          availableViews={['list', 'kanban', 'calendar']}
        />
      </Toolbar>

      {/* Filter panel */}
      <FilterPanel
        isOpen={showFilter}
        loading={loadingFilters}
        isClearing={clearingFilters}
        onClear={handleClearAll}
      >
        <div>
          <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1 flex items-center justify-between">
            <span><T>Project</T></span>
            {loadingFilters && <span className="text-[10px] text-gray-400 animate-pulse font-normal">Loading...</span>}
          </label>
          <select 
            className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2 text-xs bg-white dark:bg-[#0d1117] text-gray-700 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-[#E8612C]" 
            value={filters.projectId}
            onChange={e => setFilters({ ...filters, projectId: e.target.value })}
            disabled={loadingFilters}
          >
            <option value=""><T>All Projects</T></option>
            {(filterOptions.projects?.length > 0 ? filterOptions.projects : projects || []).map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1 flex items-center justify-between">
            <span><T>Status</T></span>
            {loadingFilters && <span className="text-[10px] text-gray-400 animate-pulse font-normal">Loading...</span>}
          </label>
          <select 
            className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2 text-xs bg-white dark:bg-[#0d1117] text-gray-700 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-[#E8612C]" 
            value={filters.status}
            onChange={e => setFilters({ ...filters, status: e.target.value })}
            disabled={loadingFilters}
          >
            <option value=""><T>All Statuses</T></option>
            {(filterOptions.statuses?.length > 0
              ? filterOptions.statuses
              : ["Pending", "In Progress", "Completed", "To Do", "Review"]
            ).map(st => (
              <option key={st} value={st}><T>{st}</T></option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1 flex items-center justify-between">
            <span><T>Priority</T></span>
            {loadingFilters && <span className="text-[10px] text-gray-400 animate-pulse font-normal">Loading...</span>}
          </label>
          <select 
            className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg p-2 text-xs bg-white dark:bg-[#0d1117] text-gray-700 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-[#E8612C]" 
            value={filters.priority}
            onChange={e => setFilters({ ...filters, priority: e.target.value })}
            disabled={loadingFilters}
          >
            <option value=""><T>All Priorities</T></option>
            {(filterOptions.priorities?.length > 0
              ? filterOptions.priorities
              : ["Low", "Medium", "High", "Urgent"]
            ).map(pr => (
              <option key={pr} value={pr}><T>{pr}</T></option>
            ))}
          </select>
        </div>
      </FilterPanel>

      {/* Content */}
      <TableCard>
        {view === "kanban" ? <TaskKanbanView tasks={filteredTasks} /> :
         view === "calendar" ? <TaskCalendarView tasks={filteredTasks} /> :
         <TaskListView tasks={filteredTasks} />}
      </TableCard>
    </PageLayout>
  );
};

export default TaskPage;